import {
  useSalesServiceSalesOrdersModificationPriceCreate,
  useSalesServiceSalesOrdersPartialUpdate,
  useSalesServiceSalesProductsPriceCreate
} from '@insurance/api/queries'
import {
  ContractorType,
  EditTravelInsurance,
  PriceMedical,
  Status9e7Enum
} from '@insurance/api/requests'
import { useModifyPayment } from '@insurance/hooks'
import { ProductCode } from '@insurance/types'
import { DEFAULT_CURRENCY } from '@shared/constants'
import { showApiErrors } from '@shared/utils'
import { Form } from 'antd'

import { useHealthInsuranceValues } from './HealthInsuranceContext'
import { HealthFormValues } from './types'
import {
  normalizeCommonData,
  normalizeEditOrderData,
  normalizeModifyOrderData,
  removeHealthValuesFromStorage
} from './utils'
import { InsurancePaymentControls } from '../InsurancePaymentControls'

type Props = {
  steps: number
  onReadyToSaveData?: () => void
}

export const HealthInsuranceControls = ({
  steps,
  onReadyToSaveData
}: Props) => {
  const formInstance = Form.useFormInstance()
  const { order, isEditable } = useHealthInsuranceValues()

  const step = Form.useWatch('step', { preserve: true })
  const price = Form.useWatch('price', { preserve: true })
  const orderPrice = Form.useWatch('order_price', { preserve: true })
  const contractor = Form.useWatch('contractor', { preserve: true })

  const isLegalEntity = contractor?.content_type === ContractorType.LEGALENTITY

  const { payPermission } = useModifyPayment({ formInstance })

  const canPay =
    !isEditable || (payPermission && price?.amount > orderPrice?.amount)

  const { mutate: calculate, isPending: isCalculatingLoading } =
    useSalesServiceSalesProductsPriceCreate({
      onError: (error) => showApiErrors(error, formInstance)
    })

  const { mutate: calculateModify, isPending: isCalculatingModifyLoading } =
    useSalesServiceSalesOrdersModificationPriceCreate({
      onSuccess: (data) => {
        formInstance.setFieldValue('price', {
          currency: DEFAULT_CURRENCY,
          amount: Number(Number(data?.price_data?.amount)?.toFixed(2))
        })
      },
      onError: (error) => showApiErrors(error, formInstance)
    })

  const { mutate: updateOrder, isPending: isUpdateOrderLoading } =
    useSalesServiceSalesOrdersPartialUpdate({
      onError: (error) => showApiErrors(error, formInstance)
    })

  const runCalculate = (checkIDNP: boolean, onSuccess: () => void) => {
    const values = formInstance.getFieldsValue(true) as HealthFormValues

    calculate(
      {
        code: ProductCode.MEDICAL_TRAVEL,
        requestBody: normalizeCommonData(values, false, checkIDNP)
      },
      {
        onSuccess: (data) => {
          const draftOrderId = (data as PriceMedical)?.order?.id

          formInstance.setFieldValue('order_id', draftOrderId)

          if (draftOrderId) runUpdate(draftOrderId, onSuccess)
        }
      }
    )
  }

  const runUpdate = (draftOrderId: number, onSuccess: () => void) => {
    const values = formInstance.getFieldsValue(true) as HealthFormValues

    updateOrder(
      {
        id: draftOrderId,
        requestBody: {
          stage: step + 1,
          status: Status9e7Enum.DATA_ENTRY,
          medical_travel: normalizeCommonData(
            values,
            false,
            false
          ) as EditTravelInsurance
        }
      },
      {
        onSuccess: (data) => {
          formInstance.setFieldValue('price', {
            currency: DEFAULT_CURRENCY,
            amount: Number(Number(data?.amount)?.toFixed(2))
          })
          window.scrollTo({ top: 0, behavior: 'smooth' })
          onSuccess()
        }
      }
    )
  }

  const runStep = (checkIDNP: boolean, onSuccess: () => void) => {
    const values = formInstance.getFieldsValue(true) as HealthFormValues

    if (order?.id) {
      calculateModify(
        { id: order.id, requestBody: normalizeModifyOrderData(values) },
        { onSuccess }
      )
    } else if (values.order_id) {
      runUpdate(values.order_id, onSuccess)
    } else {
      runCalculate(checkIDNP, onSuccess)
    }
  }

  const handleCalculate = async (onSuccess: () => void) => {
    await formInstance.validateFields()
    runStep(false, onSuccess)
  }

  const handleGoToCustomer = async (onSuccess: () => void) => {
    await formInstance.validateFields()
    runStep(true, onSuccess)
  }

  return (
    <InsurancePaymentControls
      steps={steps}
      onReadyToSaveData={onReadyToSaveData}
      removeFromStorage={() => removeHealthValuesFromStorage(order?.id)}
      keepDraftOrderOnPrevStep
      handleCalculate={handleCalculate}
      isCalculatingLoading={
        isCalculatingLoading ||
        isCalculatingModifyLoading ||
        isUpdateOrderLoading
      }
      isLegalEntity={isLegalEntity}
      disabledPay={!canPay}
      errorUnwrapKeys={['medical_travel']}
      middleStep={{
        onClick: handleGoToCustomer,
        isLoading:
          isCalculatingLoading ||
          isCalculatingModifyLoading ||
          isUpdateOrderLoading,
        disabled: !canPay
      }}
      normalizeOrderData={(values) => ({
        id: (values as HealthFormValues).order_id as number,
        requestBody: {
          medical_travel: normalizeEditOrderData(values as HealthFormValues)
        }
      })}
      modify={
        isEditable
          ? {
              order,
              normalizeOrderData: (values) => ({
                id: order!.id!,
                requestBody: normalizeModifyOrderData(
                  values as HealthFormValues
                )
              })
            }
          : undefined
      }
    />
  )
}
